import { create } from "zustand";

import { ENDPOINT_PROETHICS_SUMMATIVE } from "../constants";
import { DEFAULT_SCENARIO_ID } from "../data/scenario-registry";
import { createSSEParser } from "../helpers/parseUIMessageStream";
import { streamProethicsPost } from "../helpers/proethicsApi";
import type {
	ArcStage,
	ProethicsScenarioId,
	SummativeResult,
	TranscriptMessage,
} from "../types";

/**
 * Holds the simulation runs - conversation and End-flow hand-off - in a
 * module-level store so they survive item navigation within a session (the
 * simulation card unmounts on Next/Prev). Spans three separate trees: the
 * simulation card, the session's Next button (StudySessionBottombarContainer),
 * and the reflect/report step.
 *
 * Each scenario keeps its own run in `runs`. The active scenario's run is also
 * mirrored at the top level so consumers can read `transcript`/`summative`/etc.
 * directly; `setScenario` snapshots the outgoing run into `runs` and restores
 * the incoming one, so navigating back to an earlier scenario shows the chat and
 * report exactly as the learner left them.
 *
 * - useProethicsConversation reads/writes the active conversation slice.
 * - End flips `hasEnded` (which unlocks Next).
 * - Next triggers `submitSummative` (once per scenario), streaming the report.
 * - The reflect step reads `summative` for its scenario.
 *
 * Reset happens on new-session creation (useSession), not on mount.
 */
type Run = {
	hasStarted: boolean;
	transcript: TranscriptMessage[];
	arcStage: ArcStage;
	isStreaming: boolean;
	threadId: string | null;
	/**
	 * Id of the message whose audio just arrived over SSE. Only this one
	 * autoplays; rehydrated bubbles (item re-mount after navigation) must not.
	 * Consumed (cleared) by the audio row on mount.
	 */
	autoplayMessageId: string | null;
	hasEnded: boolean;
	summative: Partial<SummativeResult>;
	isSubmittingSummative: boolean;
	hasSubmitted: boolean;
};

type State = Run & {
	scenarioId: ProethicsScenarioId;
	// Persisted runs for every scenario except the active one (which lives at the
	// top level). Snapshotted/restored by `setScenario`.
	runs: Partial<Record<ProethicsScenarioId, Run>>;
	startConversation: () => void;
	endConversation: () => void;
	submitSummative: () => Promise<void>;
	setScenario: (scenarioId: ProethicsScenarioId) => void;
	reset: () => void;
};

const INITIAL_RUN: Run = {
	hasStarted: false,
	transcript: [],
	arcStage: "confident",
	isStreaming: false,
	threadId: null,
	autoplayMessageId: null,
	hasEnded: false,
	summative: {},
	isSubmittingSummative: false,
	hasSubmitted: false,
};

const INITIAL = {
	...INITIAL_RUN,
	scenarioId: DEFAULT_SCENARIO_ID as ProethicsScenarioId,
	runs: {} as Partial<Record<ProethicsScenarioId, Run>>,
};

// Snapshots just the run slice out of the flat state, dropping scenarioId/runs
// and the actions, so it can be stashed in `runs`.
const pickRun = (s: Run): Run => ({
	hasStarted: s.hasStarted,
	transcript: s.transcript,
	arcStage: s.arcStage,
	isStreaming: s.isStreaming,
	threadId: s.threadId,
	autoplayMessageId: s.autoplayMessageId,
	hasEnded: s.hasEnded,
	summative: s.summative,
	isSubmittingSummative: s.isSubmittingSummative,
	hasSubmitted: s.hasSubmitted,
});

export const useProethicsFlowStore = create<State>((set, get) => {
	// Applies a patch to a scenario's run whether or not it is still the active
	// one, so a summative that streams in after the learner navigates away lands
	// on the scenario it belongs to instead of corrupting the new active run.
	const patchRun = (scenarioId: ProethicsScenarioId, patch: Partial<Run>) => {
		const state = get();
		if (state.scenarioId === scenarioId) {
			set(patch);
			return;
		}
		const run = state.runs[scenarioId];
		if (!run) {
			return;
		}
		set({ runs: { ...state.runs, [scenarioId]: { ...run, ...patch } } });
	};

	return {
		...INITIAL,

		startConversation: () => set({ hasStarted: true }),

		endConversation: () => set({ hasEnded: true }),

		submitSummative: async () => {
			// Only ever submit one summative per scenario run.
			if (get().hasSubmitted || get().isSubmittingSummative) {
				return;
			}
			const scenarioId = get().scenarioId;
			patchRun(scenarioId, { isSubmittingSummative: true, hasSubmitted: true });

			const transcript = get().transcript;
			const readSummative = () =>
				get().scenarioId === scenarioId
					? get().summative
					: (get().runs[scenarioId]?.summative ?? {});
			const feed = createSSEParser((part) => {
				if (part.type === "data-summative-stream") {
					patchRun(scenarioId, {
						summative: { ...readSummative(), ...part.data },
					});
				} else if (part.type === "data-final-result") {
					patchRun(scenarioId, { summative: part.data });
				}
			});

			try {
				await streamProethicsPost(
					ENDPOINT_PROETHICS_SUMMATIVE,
					{
						scenarioId,
						transcript: transcript.map(({ role, text }) => ({ role, text })),
					},
					feed,
				);
			} catch {
				patchRun(scenarioId, {
					summative: {
						criteria: [],
						feedback: "Could not load the report. Please try again.",
					},
				});
			} finally {
				patchRun(scenarioId, { isSubmittingSummative: false });
			}
		},

		setScenario: (scenarioId) => {
			// Re-entering the active scenario keeps the run live. Switching stashes the
			// current run and restores the target's (or a fresh one), so each
			// scenario's chat and report persist across navigation.
			const state = get();
			if (state.scenarioId === scenarioId) {
				return;
			}
			const runs = { ...state.runs, [state.scenarioId]: pickRun(state) };
			const nextRun = runs[scenarioId] ?? INITIAL_RUN;
			set({ ...nextRun, scenarioId, runs });
		},

		reset: () => set(INITIAL),
	};
});
